/**
 * Blind Schnorr signatures on secp256k1 — Cashero's core ecash cryptography.
 *
 * Implements the Cashu blind signature scheme (NUT-00 compatible):
 *   - Mint (signer) holds private key `a`, public key `A = a·G`
 *   - Wallet (user) blinds a secret: `B_ = hashToCurve(secret) + r·G`
 *   - Mint signs blindly: `C_ = a · B_`
 *   - Wallet unblinds: `C = C_ - r·A = a·hashToCurve(secret)`
 *   - Anyone verifies: `C == a·hashToCurve(secret)` (mint-side with privkey)
 */

import { secp256k1, schnorr } from '@noble/curves/secp256k1';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex, hexToBytes, randomBytes, utf8ToBytes } from '@noble/hashes/utils';

/** secp256k1 curve point — uses noble's ProjectivePoint class or schnorr fallback */
export type Point = ReturnType<typeof getPointClass>;

// secp256k1 field prime: 2^256 - 2^32 - 2^9 - 2^8 - 2^7 - 2^6 - 2^4 - 1
const SECP256K1_P = 0xfffffffffffffffffffffffffffffffffffffffffffffffffffffffefffffc2fn;

/** Modular exponentiation for the field prime. */
function modPow(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = 1n;
  let b = ((base % mod) + mod) % mod;
  let e = exp;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % mod;
    b = (b * b) % mod;
    e >>= 1n;
  }
  return result;
}

/** Lazy-resolve the secp256k1 point class (ProjectivePoint). */
let _PointClass: any = null;
function getPointClass(): any {
  if (_PointClass) return _PointClass;
  // Try multiple API paths for @noble/curves v2
  // schnorr.Point IS the point class (has fromAffine, fromHex, BASE, multiply)
  _PointClass =
    secp256k1?.ProjectivePoint ??
    schnorr?.Point ??
    schnorr?.Point?.ProjectivePoint ??
    schnorr?.Point?.Point;
  if (!_PointClass) {
    // Last resort: the Point property on the weierstrass return
    _PointClass = (schnorr as any).Point?.Point;
  }
  if (!_PointClass) throw new Error('secp256k1 Point class not found');
  return _PointClass;
}

/** Reusable helper: create a point from hex. */
export function pointFromHex(hex: string): Point {
  return getPointClass().fromHex(hex);
}

/** A blinded message (sent from wallet to mint) */
export interface BlindedMessage {
  /** The blinded point B_ = hashToCurve(secret) + r·G (hex-encoded, uncompressed) */
  B_: string;
  /** The amount this blinded message is for (piconero) */
  amount: number;
}

/** A blinded signature (returned from mint to wallet) */
export interface BlindedSignature {
  /** The blinded signature point C_ = a·B_ (hex-encoded, uncompressed) */
  C_: string;
  /** The keyset ID this signature belongs to */
  id: string;
}

/** Internal blinded message data (kept by wallet during the blind-sign process) */
export interface RawBlindedMessage {
  /** The blinded point sent to mint */
  B_: Point;
  /** The blinding factor (scalar) */
  r: bigint;
  /** The secret (raw bytes) */
  secret: Uint8Array;
  /** The amount this message represents */
  amount: number;
}

/** An unblinded signature proof (the final ecash token component) */
export interface UnblindedSignature {
  /** The unblinded signature point C (hex-encoded) */
  C: string;
  /** The secret (64-char hex string) */
  secret: string;
  /** The keyset ID */
  id: string;
  /** The amount this proof represents (piconero) */
  amount: number;
}

// ----------------------------------------------------------------
// Domain separator for hash-to-curve (Cashu NUT-00 spec)
// ----------------------------------------------------------------
const DOMAIN_SEPARATOR = utf8ToBytes('Secp256k1_HashToCurve_Cashu_');

/**
 * Hash a secret value to a secp256k1 curve point.
 * Uses SHA-256 with a domain separator and try-and-increment (Cashu NUT-00).
 */
export function hashToCurve(secret: Uint8Array): Point {
  const base = sha256(new Uint8Array([...DOMAIN_SEPARATOR, ...secret]));
  const maxIterations = 2 ** 16;

  for (let i = 0; i < maxIterations; i++) {
    // Use big-endian bytes for platform-independent counter
    const counterBytes = new Uint8Array([
      (i >>> 24) & 0xff,
      (i >>> 16) & 0xff,
      (i >>> 8) & 0xff,
      i & 0xff,
    ]);
    const hash = sha256(new Uint8Array([...base, ...counterBytes]));

    // Convert hash to x-coordinate (mod p)
    let x = 0n;
    for (const b of hash) x = (x << 8n) | BigInt(b);
    x %= SECP256K1_P;

    // Compute y² = x³ + 7 mod p
    const ySquared = (x * x * x + 7n) % SECP256K1_P;

    // For p ≡ 3 (mod 4): y = (y²)^((p+1)/4) mod p
    const y = modPow(ySquared, (SECP256K1_P + 1n) / 4n, SECP256K1_P);

    // Verify y² == (y*y) mod p — skip if no valid point
    if ((y * y) % SECP256K1_P !== ySquared) continue;

    // Construct the point using the resolved point class
    const PC = getPointClass();
    try {
      return PC.fromAffine({ x, y });
    } catch {
      continue;
    }
  }

  throw new Error('hashToCurve: failed to find a curve point after 2^16 iterations');
}

/** Generate a random 32-byte secret (64-char hex string). */
export function generateSecret(): string {
  return bytesToHex(randomBytes(32));
}

/** Convert a Uint8Array private key to a bigint scalar. */
export function bytesToScalar(bytes: Uint8Array): bigint {
  // In noble v2, reduce bytes mod n manually
  const n = secp256k1.CURVE.n;
  let result = 0n;
  for (const b of bytes) {
    result = (result << 8n) | BigInt(b);
  }
  return result % n;
}

/** Create a random blinding factor (scalar). */
export function randomBlindingFactor(): bigint {
  return bytesToScalar(secp256k1.utils.randomPrivateKey());
}

/**
 * Blind a secret message to produce a blinded point and blinding factor.
 *
 * B_ = hashToCurve(secret) + r·G
 */
export function blindMessage(
  secret: Uint8Array,
  r?: bigint,
): RawBlindedMessage {
  const Y = hashToCurve(secret);
  const blindingFactor = r ?? randomBlindingFactor();
  const rG = getPointClass().BASE.multiply(blindingFactor);
  const B_ = Y.add(rG);
  return { B_, r: blindingFactor, secret, amount: 0 };
}

/** Create a random blinded message with a fresh secret and blinding factor. */
export function createRandomBlindedMessage(): RawBlindedMessage {
  const secretStr = generateSecret();
  const secretBytes = utf8ToBytes(secretStr);
  return blindMessage(secretBytes);
}

/**
 * Mint: Create a blind signature on a blinded message.
 *
 * C_ = a · B_  (scalar multiplication of the blinded point by the mint's private key)
 */
export function createBlindSignature(
  B_: Point,
  privateKey: Uint8Array,
  id: string,
): BlindedSignature {
  const a = bytesToScalar(privateKey);
  const C_ = B_.multiply(a);
  return { C_: bytesToHex(C_.toRawBytes(false)), id };
}

/**
 * Wallet: Unblind a signature to get the final proof.
 *
 * C = C_ - r · A   (where A = a·G is the mint's public key)
 */
export function unblindSignature(C_: Point, r: bigint, A: Point): Point {
  const rA = A.multiply(r);
  return C_.subtract(rA);
}

/**
 * Wallet: Construct a full unblinded signature from a blinded signature response.
 */
export function constructUnblindedSignature(
  blindSig: BlindedSignature,
  r: bigint,
  secret: Uint8Array,
  pubKey: Point,
  amount: number,
): UnblindedSignature {
  const C_ = getPointClass().fromHex(blindSig.C_);
  const C = unblindSignature(C_, r, pubKey);
  return {
    C: bytesToHex(C.toRawBytes(false)),
    secret: bytesToHex(secret),
    id: blindSig.id,
    amount,
  };
}

/**
 * Mint-side verification: checks C == a · hashToCurve(secret).
 *
 * @param proof - The unblinded signature to verify
 * @param privKey - The mint's private key for this amount's denomination
 */
export function verifyProof(proof: UnblindedSignature, privKey: Uint8Array): boolean {
  const Y = hashToCurve(utf8ToBytes(proof.secret));
  const a = bytesToScalar(privKey);
  const aY = Y.multiply(a);
  const C = getPointClass().fromHex(proof.C);
  return aY.equals(C);
}

/**
 * Serialize a blinded message for transport.
 */
export function serializeBlindedMessage(msg: RawBlindedMessage): BlindedMessage {
  return {
    B_: bytesToHex(msg.B_.toRawBytes(false)),
    amount: msg.amount,
  };
}

/**
 * Parse a blinded message from transport. Returns the point and amount.
 * Note: the wallet keeps the blinding factor (r) and secret locally — not sent to mint.
 */
export function parseBlindedMessage(msg: BlindedMessage): { B_: Point; amount: number } {
  return {
    B_: getPointClass().fromHex(msg.B_),
    amount: msg.amount,
  };
}
